/**
 * =============================================================================
 * Hook: useAffiliateImport
 * Versão: 1.0.0
 *
 * Descrição:
 *   Pipeline de importação em massa de afiliados via CSV.
 *
 *   Etapas controladas pelo wizard:
 *     1. validateRows()  — checa formato, e-mails duplicados no CSV e
 *                          colisão com `affiliates` no tenant ativo.
 *     2. importAffiliates() — para cada linha válida e nova, chama a RPC
 *                             existente `create_affiliate`. Quando solicitada
 *                             aprovação imediata, atualiza a adesão para
 *                             `active`. Aplica observações em `affiliates.notes`.
 *
 *   Reaproveita o desenho funcional do `useListImport` mas é totalmente
 *   independente — não cria registros em `campaign_lists` nem mexe em
 *   `contacts`/blacklist LGPD.
 * =============================================================================
 */
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import {
  AffiliateCSVRow,
  AffiliateRowValidation,
  isValidEmail,
  normalizeAffiliatePhoneInput,
  detectDocumentType,
  AffiliateDocumentType,
} from '@/lib/affiliate-csv-utils';

// =============================================================================
// TIPOS PÚBLICOS
// =============================================================================

export interface AffiliateValidationStats {
  total: number;
  valid: number;
  invalid: number;
  duplicatesInCSV: number;
  existingInSystem: number;
  newAffiliates: number;
}

export interface AffiliateImportProgress {
  current: number;
  total: number;
  phase: 'validating' | 'importing' | 'done';
  message: string;
}

export interface AffiliateImportResult {
  /** true se a importação foi enfileirada com sucesso (pipeline assíncrono PGMQ). */
  success: boolean;
  /**
   * Modo assíncrono (v2): contadores reais virão do worker. Os campos abaixo
   * são preservados para compatibilidade visual com a etapa de resultado.
   */
  created: number;     // = items enfileirados (queued_items) no fluxo async
  approved: number;    // 0 enquanto o worker não roda — a tela de Listas atualiza em real-time
  skipped: number;     // duplicatas + existentes detectados na pré-validação local
  failed: number;      // linhas inválidas detectadas pela validação local
  errors: Array<{ rowIndex: number; email: string; message: string }>;

  // ----- Específicos do fluxo assíncrono (v2) -----
  /** Sempre true no fluxo PGMQ — sinaliza para a UI que o processamento é em background. */
  enqueued?: boolean;
  /** ID do job criado em affiliate_import_jobs. */
  jobId?: string | null;
  /** Quantidade de lotes PGMQ disparados. */
  queuedBatches?: number;
  /** Quantidade de itens (linhas válidas) que entraram na fila para processamento. */
  queuedItems?: number;
}

// =============================================================================
// HOOK
// =============================================================================

export function useAffiliateImport() {
  const { currentTenant, user } = useAuth();

  const [validatedRows, setValidatedRows] = useState<AffiliateRowValidation[]>([]);
  const [validationStats, setValidationStats] = useState<AffiliateValidationStats | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<AffiliateImportProgress | null>(null);

  // ---------------------------------------------------------------------------
  // Validação por linha
  // ---------------------------------------------------------------------------
  const validateRows = useCallback(
    async (rows: AffiliateCSVRow[], defaultCountryCode = '55'): Promise<AffiliateRowValidation[]> => {
      if (!currentTenant?.id) throw new Error('Tenant não identificado');

      setIsValidating(true);
      setProgress({
        current: 0,
        total: rows.length,
        phase: 'validating',
        message: 'Buscando afiliados existentes...',
      });

      try {
        // 1) Carrega e-mails já cadastrados no tenant para detecção de colisão
        const { data: existing, error: fetchErr } = await supabase
          .from('affiliates')
          .select('id, email')
          .eq('tenant_id', currentTenant.id);
        if (fetchErr) throw fetchErr;

        const existingMap = new Map<string, string>(); // email -> id
        existing?.forEach((a) => {
          if (a.email) existingMap.set(a.email.toLowerCase().trim(), a.id);
        });

        const seenInCSV = new Map<string, number>(); // email -> rowIndex
        const validations: AffiliateRowValidation[] = [];

        for (let i = 0; i < rows.length; i++) {
          if (i % 50 === 0 || i === rows.length - 1) {
            setProgress({
              current: i + 1,
              total: rows.length,
              phase: 'validating',
              message: `Validando linha ${i + 1} de ${rows.length}...`,
            });
          }

          const row = rows[i];
          const errors: string[] = [];

          // Nome
          const nome = (row.nome || '').trim();
          if (!nome) errors.push('Nome é obrigatório');
          else if (nome.length < 2) errors.push('Nome muito curto');
          else if (nome.length > 200) errors.push('Nome excede 200 caracteres');

          // E-mail
          const emailNormalized = (row.email || '').trim().toLowerCase();
          if (!emailNormalized) errors.push('E-mail é obrigatório');
          else if (!isValidEmail(emailNormalized)) errors.push('E-mail inválido');

          // Telefone (opcional)
          const phoneResult = normalizeAffiliatePhoneInput(row.telefone || '', defaultCountryCode);
          if (!phoneResult.valid) errors.push(phoneResult.error || 'Telefone inválido');

          // Documento (opcional). Se informado, exige tipo coerente.
          const docDigits = (row.documento || '').replace(/\D/g, '');
          let docType: AffiliateDocumentType | null = null;
          if (docDigits) {
            const informedType = (row.tipo_documento || '').trim().toLowerCase();
            if (informedType === 'cpf' || informedType === 'cnpj') {
              docType = informedType as AffiliateDocumentType;
            } else if (informedType && informedType !== '-') {
              errors.push('tipo_documento deve ser "cpf" ou "cnpj"');
            } else {
              // Auto-detecta se o usuário não informou
              docType = detectDocumentType(docDigits);
              if (!docType) errors.push('Documento inválido (use 11 dígitos para CPF ou 14 para CNPJ)');
            }
            if (docType === 'cpf' && docDigits.length !== 11) {
              errors.push('CPF deve ter 11 dígitos');
            }
            if (docType === 'cnpj' && docDigits.length !== 14) {
              errors.push('CNPJ deve ter 14 dígitos');
            }
          }

          // Observações (opcional, limite leve)
          if ((row.observacoes || '').length > 2000) {
            errors.push('Observações excedem 2000 caracteres');
          }

          // Duplicata no CSV (por e-mail)
          let isDuplicateInCSV = false;
          let duplicateOfIndex: number | undefined;
          if (emailNormalized && isValidEmail(emailNormalized)) {
            const firstIdx = seenInCSV.get(emailNormalized);
            if (firstIdx !== undefined) {
              isDuplicateInCSV = true;
              duplicateOfIndex = firstIdx;
              errors.push('E-mail duplicado no arquivo');
            } else {
              seenInCSV.set(emailNormalized, i);
            }
          }

          // Existência no sistema
          const existingAffiliateId = emailNormalized ? existingMap.get(emailNormalized) : undefined;

          validations.push({
            rowIndex: i,
            row,
            isValid: errors.length === 0,
            errors,
            phoneNormalized: phoneResult.normalized || undefined,
            emailNormalized: emailNormalized || undefined,
            documentNormalized: docDigits || undefined,
            documentType: docType,
            isDuplicateInCSV,
            duplicateOfIndex,
            existsInSystem: !!existingAffiliateId,
            existingAffiliateId,
          });
        }

        const stats: AffiliateValidationStats = {
          total: validations.length,
          valid: validations.filter((v) => v.isValid).length,
          invalid: validations.filter((v) => !v.isValid).length,
          duplicatesInCSV: validations.filter((v) => v.isDuplicateInCSV).length,
          existingInSystem: validations.filter((v) => v.existsInSystem).length,
          newAffiliates: validations.filter((v) => v.isValid && !v.existsInSystem).length,
        };

        setValidatedRows(validations);
        setValidationStats(stats);
        logger.info('AffiliateImport', 'Validação concluída', stats);
        return validations;
      } finally {
        setIsValidating(false);
        setProgress(null);
      }
    },
    [currentTenant?.id],
  );

  /**
   * Atualiza um campo de uma linha validada (edição inline na tabela do step 2)
   * sem revalidar — o usuário deve clicar em "Revalidar".
   */
  const updateValidatedRow = useCallback((rowIndex: number, field: string, value: string) => {
    setValidatedRows((prev) =>
      prev.map((v) =>
        v.rowIndex === rowIndex ? { ...v, row: { ...v.row, [field]: value } } : v,
      ),
    );
  }, []);

  // ---------------------------------------------------------------------------
  // Importação efetiva — pipeline ASSÍNCRONO (PGMQ + worker)
  // ---------------------------------------------------------------------------
  // RELEASE 2.0.0 (2026-05-17): substitui o loop síncrono de create_affiliate
  // por um pipeline server-side que reaproveita 100% as regras de negócio:
  //   1) create_affiliate_import_job_v2   → cria job + defaults
  //   2) populate_affiliate_import_items  → grava linhas em lotes de 500
  //   3) validate_affiliate_import_job    → revalida tudo no servidor
  //   4) approve_and_enqueue_affiliate_import_job → particiona em batches
  //      de 100 e dispara mensagens PGMQ na fila `affiliates_import`.
  // O worker `affiliate-import-worker-pgmq` consome a fila e chama a RPC
  // pública `create_affiliate` (v2.2.0 — padrão híbrido) para cada item.
  // ---------------------------------------------------------------------------
  const importAffiliates = useCallback(
    async (params: {
      programId: string;
      validations: AffiliateRowValidation[];
      forceApprove: boolean;
      /** Arquivo CSV original (opcional). Quando fornecido, é salvo no bucket 'affiliate/imports/'. */
      sourceFile?: File;
      /** Nome do arquivo original (opcional). */
      sourceFileName?: string;
      /** Nome amigável da importação (default: nome do arquivo sem extensão). */
      importName?: string;
      /** DDI padrão para telefones sem código de país. Default: 55. */
      defaultCountryCode?: string;
    }): Promise<AffiliateImportResult> => {
      const {
        programId,
        validations,
        forceApprove,
        sourceFile,
        sourceFileName,
        importName,
        defaultCountryCode = '55',
      } = params;

      if (!currentTenant?.id || !user?.id) {
        return {
          success: false,
          created: 0,
          approved: 0,
          skipped: 0,
          failed: 0,
          errors: [{ rowIndex: -1, email: '', message: 'Usuário ou tenant não identificado' }],
        };
      }

      setIsImporting(true);
      const errors: AffiliateImportResult['errors'] = [];

      // Métricas locais derivadas da validação client-side (apenas informativas).
      const localInvalid = validations.filter((v) => !v.isValid).length;
      const localDuplicates = validations.filter((v) => v.isDuplicateInCSV).length;
      const localExisting = validations.filter((v) => v.existsInSystem).length;
      const localValid = validations.filter(
        (v) => v.isValid && !v.isDuplicateInCSV && !v.existsInSystem,
      ).length;

      try {
        // -------------------------------------------------------------------
        // 1) Upload do CSV original (best-effort)
        // -------------------------------------------------------------------
        let uploadedPath: string | null = null;
        if (sourceFile) {
          try {
            const ext = (sourceFileName?.split('.').pop() || 'csv').toLowerCase();
            const uuid =
              (globalThis.crypto as any)?.randomUUID?.() ??
              `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const path = `imports/${currentTenant.id}/${uuid}.${ext}`;
            const { error: upErr } = await supabase.storage
              .from('affiliate')
              .upload(path, sourceFile, {
                contentType: sourceFile.type || 'text/csv',
                upsert: false,
              });
            if (upErr) {
              logger.warn('AffiliateImport', 'Falha no upload do CSV (segue sem arquivo)', upErr);
            } else {
              uploadedPath = path;
            }
          } catch (e) {
            logger.warn('AffiliateImport', 'Exceção no upload do CSV', e as any);
          }
        }

        // -------------------------------------------------------------------
        // 2) Cria job (v2)
        // -------------------------------------------------------------------
        setProgress({
          current: 0,
          total: validations.length,
          phase: 'importing',
          message: 'Criando job de importação...',
        });

        const { data: jobIdData, error: jobErr } = await (supabase as any).rpc(
          'create_affiliate_import_job_v2',
          {
            p_name: importName?.trim() || sourceFileName || null,
            p_program_id: programId,
            p_source_file_path: uploadedPath,
            p_source_file_name: sourceFileName ?? null,
            p_force_approve: !!forceApprove,
            p_default_country_code: defaultCountryCode,
            p_options: {
              client_invalid: localInvalid,
              client_duplicates: localDuplicates,
              client_existing: localExisting,
              created_by_admin: user.id,
            },
          },
        );
        if (jobErr) throw jobErr;
        const jobId = (jobIdData as string) ?? null;
        if (!jobId) throw new Error('RPC create_affiliate_import_job_v2 não retornou jobId');

        // -------------------------------------------------------------------
        // 3) Popula os itens em lotes de 500 (linhas brutas — a validação
        //    server-side é executada na etapa 4).
        // -------------------------------------------------------------------
        const CHUNK = 500;
        const rawRows = validations.map((v) => ({
          // Chaves PT-BR canônicas exigidas pela RPC validate_affiliate_import_job
          nome: v.row.nome ?? '',
          email: v.row.email ?? '',
          telefone: v.row.telefone ?? '',
          documento: v.row.documento ?? '',
          tipo_documento: v.row.tipo_documento ?? '',
          observacoes: v.row.observacoes ?? '',
        }));

        for (let offset = 0; offset < rawRows.length; offset += CHUNK) {
          const chunk = rawRows.slice(offset, offset + CHUNK);
          setProgress({
            current: Math.min(offset + chunk.length, rawRows.length),
            total: rawRows.length,
            phase: 'importing',
            message: `Enviando linhas ao servidor (${offset + chunk.length}/${rawRows.length})...`,
          });
          const { error: popErr } = await (supabase as any).rpc(
            'populate_affiliate_import_items',
            {
              p_job_id: jobId,
              p_rows: chunk,
              p_offset: offset,
            },
          );
          if (popErr) throw popErr;
        }

        // -------------------------------------------------------------------
        // 4) Validação server-side (single source of truth)
        // -------------------------------------------------------------------
        setProgress({
          current: rawRows.length,
          total: rawRows.length,
          phase: 'importing',
          message: 'Validando dados no servidor...',
        });
        const { error: valErr } = await (supabase as any).rpc(
          'validate_affiliate_import_job',
          { p_job_id: jobId },
        );
        if (valErr) throw valErr;

        // -------------------------------------------------------------------
        // 5) Aprovação + enfileiramento PGMQ
        // -------------------------------------------------------------------
        setProgress({
          current: rawRows.length,
          total: rawRows.length,
          phase: 'importing',
          message: 'Enfileirando lotes para processamento...',
        });
        const { data: enqData, error: enqErr } = await (supabase as any).rpc(
          'approve_and_enqueue_affiliate_import_job',
          { p_job_id: jobId, p_batch_size: 100 },
        );
        if (enqErr) throw enqErr;

        const enqRow = Array.isArray(enqData) ? enqData[0] : enqData;
        const queuedBatches = Number(enqRow?.batches_created ?? 0);
        const queuedItems = Number(enqRow?.items_enqueued ?? 0);

        logger.info('AffiliateImport', 'Job enfileirado', {
          jobId,
          queuedBatches,
          queuedItems,
        });

        return {
          success: true,
          enqueued: true,
          jobId,
          queuedBatches,
          queuedItems,
          // Espelhos para a UI antiga:
          created: queuedItems,
          approved: 0,
          skipped: localExisting + localDuplicates,
          failed: localInvalid,
          errors,
        };
      } catch (err: any) {
        // Extrai mensagem real (Supabase/PG retorna {message, details, hint, code})
        const message =
          (err && (err.message || err.details || err.hint)) ||
          (typeof err === 'string' ? err : null) ||
          (() => { try { return JSON.stringify(err); } catch { return 'Erro desconhecido'; } })();
        logger.error('AffiliateImport', 'Falha no pipeline assíncrono', message);
        errors.push({ rowIndex: -1, email: '', message });
        return {
          success: false,
          enqueued: false,
          created: 0,
          approved: 0,
          skipped: localExisting + localDuplicates,
          failed: localInvalid + localValid,
          errors,
        };
      } finally {
        setIsImporting(false);
        setProgress({
          current: validations.length,
          total: validations.length,
          phase: 'done',
          message: 'Importação enfileirada — acompanhe o progresso em Listas → Afiliados.',
        });
      }
    },
    [currentTenant?.id, user?.id],
  );

  const reset = useCallback(() => {
    setValidatedRows([]);
    setValidationStats(null);
    setIsValidating(false);
    setIsImporting(false);
    setProgress(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Hooks de progresso/acompanhamento (alimentam o novo AffiliateProgressStep
  // e a ação "Continuar aprovação" da listagem).
  // RELEASE 2026-05-17 v2.1.0: adiciona useJobDetails / useJobItems /
  //                            approveExistingJob para espelhar o pipeline
  //                            de "Negócios" (Step6Progress).
  // ---------------------------------------------------------------------------

  /**
   * Busca uma única vez (read-only) o resumo persistido do job. Usado pelo
   * fluxo "resume" do wizard para pré-carregar nome/programa/forceApprove
   * antes de pular para o step "Importação" ou "Progresso".
   */
  const fetchJobSummary = useCallback(async (jobId: string) => {
    const { data, error } = await (supabase as any).rpc('get_affiliate_import_job', {
      p_job_id: jobId,
    });
    if (error) throw error;
    return data as {
      id: string;
      name: string;
      status: string;
      program_id: string | null;
      program_name: string | null;
      force_approve: boolean;
      total_rows: number;
      valid_count: number;
      invalid_count: number;
      duplicate_count: number;
      existing_count: number;
      succeeded_count: number;
      failed_count: number;
      approved_count: number;
      error_summary: Array<{ rowIndex: number; email: string; message: string }>;
    };
  }, []);

  /**
   * Aprova + enfileira um job já existente (status `awaiting_approval`).
   * É exatamente o passo final de `importAffiliates`, mas isolado para o
   * fluxo "Continuar aprovação" que reabre o wizard em jobs antigos.
   */
  const approveExistingJob = useMutation({
    mutationFn: async (jobId: string) => {
      const { data, error } = await (supabase as any).rpc(
        'approve_and_enqueue_affiliate_import_job',
        { p_job_id: jobId, p_batch_size: 100 },
      );
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return {
        jobId,
        batches_created: Number(row?.batches_created ?? 0),
        items_enqueued: Number(row?.items_enqueued ?? 0),
      };
    },
  });

  /**
   * Polling do detalhe de progresso: chama `get_affiliate_import_job_details`
   * (já existente — SECURITY DEFINER). Faz polling a cada 3s enquanto o job
   * estiver em estados ativos; cai para 15s em estados terminais.
   *
   * A RPC devolve { job, batches[], items[] }. Aqui já normalizamos um
   * `batches_summary` para a UI consumir no mesmo formato que Step6Progress
   * de "Negócios" usa (total / enqueued / processing / completed / failed /
   * stalled / pending), além de derivar `last_batch_update` e
   * `last_batch_error`.
   */
  function useJobDetails(
    jobId: string | null,
    options?: { refetchInterval?: number | ((q: any) => number | false); enabled?: boolean },
  ) {
    return useQuery({
      queryKey: ['affiliate-import-job-details', jobId],
      enabled: !!jobId && (options?.enabled ?? true),
      refetchInterval: options?.refetchInterval ?? ((query: any) => {
        const status = query?.state?.data?.job?.status;
        if (
          status === 'completed' ||
          status === 'completed_with_errors' ||
          status === 'failed' ||
          status === 'cancelled'
        ) {
          return 15000;
        }
        return 3000;
      }),
      queryFn: async () => {
        const { data, error } = await (supabase as any).rpc(
          'get_affiliate_import_job_details',
          { p_job_id: jobId! },
        );
        if (error) throw error;
        const raw = (data ?? {}) as { job?: any; batches?: any[]; items?: any[] };
        const batches = (raw.batches ?? []) as Array<{
          status: string;
          updated_at?: string | null;
          last_error?: string | null;
          locked_at?: string | null;
        }>;

        // Sumário agregado por status (espelha Step6Progress)
        const summary = {
          total: batches.length,
          pending: 0,
          enqueued: 0,
          processing: 0,
          completed: 0,
          failed: 0,
          stalled: 0,
        };
        let lastUpdate: string | null = null;
        let lastError: string | null = null;
        const now = Date.now();
        for (const b of batches) {
          const st = (b.status ?? 'pending') as keyof typeof summary;
          if (st in summary) summary[st] += 1;
          // "Stalled" = lote em processing/enqueued sem update há > 60s
          // (replica heurística do Step6Progress de Negócios).
          if (
            (b.status === 'processing' || b.status === 'enqueued') &&
            b.locked_at &&
            now - new Date(b.locked_at).getTime() > 60_000
          ) {
            summary.stalled += 1;
          }
          if (b.updated_at && (!lastUpdate || b.updated_at > lastUpdate)) {
            lastUpdate = b.updated_at;
          }
          if (b.last_error) lastError = b.last_error;
        }

        return {
          job: raw.job ?? null,
          batches,
          items: (raw.items ?? []) as Array<{
            row_index: number;
            status: string;
            email: string | null;
            errors: any;
            last_error: string | null;
          }>,
          batches_summary: summary,
          last_batch_update: lastUpdate,
          last_batch_error: lastError,
        };
      },
    });
  }

  /**
   * Compat: por enquanto a aba "Erros" do AffiliateProgressStep consome
   * direto `details.items` (top 100, retornado pela RPC details). Mantemos
   * este wrapper para paridade futura com o equivalente de Negócios caso
   * criemos uma RPC paginada dedicada (`get_affiliate_import_job_items`).
   */
  function useJobItems(jobId: string | null, options?: { enabled?: boolean }) {
    return useQuery({
      queryKey: ['affiliate-import-job-items', jobId],
      enabled: !!jobId && (options?.enabled ?? true),
      queryFn: async () => {
        // Hoje reaproveitamos os items já retornados por get_affiliate_import_job_details.
        // Esta query existe só para manter API simétrica caso futuramente
        // tenhamos paginação. Por ora devolve [] e a UI lê de useJobDetails.
        return [] as Array<unknown>;
      },
    });
  }

  return {
    validatedRows,
    validationStats,
    isValidating,
    isImporting,
    progress,
    validateRows,
    updateValidatedRow,
    importAffiliates,
    reset,
    // v2.1.0 — acompanhamento de progresso (espelha useDealImport)
    fetchJobSummary,
    approveExistingJob,
    useJobDetails,
    useJobItems,
  };
}
